import { ModalM } from "@common/components/modal/ModalM"
import AgentSummaryModal from "@common/liveMode/AgentSummaryModal"
import LiveChat from "@common/liveMode/LiveChat"
import { TutorGridLayoutM } from "@common/liveMode/liveV3/TutorGridLayoutM"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { hasFeature } from "@data/utility/Utils"
import { useTracks } from "@livekit/components-react"
import { Track } from "livekit-client"
import { useNavigation, useRoute } from "@react-navigation/native"
import React, { useCallback, useContext, useMemo, useState } from "react"
import { View } from "react-native"
import { connect } from "react-redux"
import TutorFocusLayoutM from "@common/liveMode/liveV3/TutorFocusLayoutM";

const TutorRoomView = (props) => {
    const {
        pinnedMode,
        setPinnedMode,
        availableWindowHeight,
        pinnedParticipantId: pinnedParticipantIdProp,
        setPinnedParticipantId: setPinnedParticipantIdProp,
        pinnedTrackSid: pinnedTrackSidProp,
        setPinnedTrackSid: setPinnedTrackSidProp,
    } = props
    const route = useRoute()
    const navigation = useNavigation()
    const liveChatUserId =
        route?.params?.liveChatUserId === "null" ||
        route?.params?.liveChatUserId === null ||
        route?.params?.liveChatUserId === undefined
            ? null
            : route?.params?.liveChatUserId
    const setLiveChatUserId = useCallback((userId) => {
        navigation?.setParams({
            liveChatUserId: userId,
        })
    }, [navigation])

    const agentCheckpointsUserId =
        route?.params?.agentCheckpointsUserId === "null" ||
        route?.params?.agentCheckpointsUserId === null ||
        route?.params?.agentCheckpointsUserId === undefined
            ? null
            : route?.params?.agentCheckpointsUserId
    const setAgentCheckpointsUserId = useCallback((userId) => {
        navigation?.setParams({
            agentCheckpointsUserId: userId,
        })
    }, [navigation])

    const dimensions = useContext(DimensionsContext)

    // Single useTracks() call for the entire tutor view — avoids O(N²) re-renders
    const tracks = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: true },
            { source: Track.Source.ScreenShare, withPlaceholder: false },
        ],
        { onlySubscribed: true }
    )

    const tracksMap = useMemo(() => {
        const map = {}
        tracks?.forEach((track) => {
            const userId = track.participant.identity?.split(".")?.[0]
            if (userId) {
                if (!map[userId]) map[userId] = []
                map[userId].push(track)
            }
        })
        return map
    }, [tracks])

    const liveSession = props.liveSessions?.find(
        (x) => x?.id === props.liveSessionId
    )
    const participants = liveSession?.users

    const otherParticipants = useMemo(
        () => participants?.filter((x) => x?.id !== props.user?.id),
        [participants, props.user?.id]
    )

    const isLifted = typeof setPinnedParticipantIdProp === "function"
    const [pinnedParticipantIdLocal, setPinnedParticipantIdLocal] = useState(
        otherParticipants?.[0]?.id
    )
    const [pinnedTrackSidLocal, setPinnedTrackSidLocal] = useState(null)

    const pinnedParticipantId = isLifted
        ? pinnedParticipantIdProp
        : pinnedParticipantIdLocal
    const setPinnedParticipantId = isLifted
        ? setPinnedParticipantIdProp
        : setPinnedParticipantIdLocal
    const pinnedTrackSid = isLifted
        ? pinnedTrackSidProp
        : pinnedTrackSidLocal
    const setPinnedTrackSid =
        isLifted && typeof setPinnedTrackSidProp === "function"
            ? setPinnedTrackSidProp
            : setPinnedTrackSidLocal

    const onParticipantClick = useCallback(
        (participantId) => {
            setPinnedParticipantId(participantId)
            setPinnedMode(true)
        },
        [setPinnedMode, setPinnedParticipantId],
    )

    const onTrackClick = useCallback(
        (trackSid) => {
            setPinnedTrackSid(trackSid)
        },
        [setPinnedTrackSid],
    )

    return (
        <View style={{ flex: 1, flexDirection: "row" }}>
            {pinnedMode ? (
                <TutorFocusLayoutM
                    participants={otherParticipants}
                    onTrackClick={onTrackClick}
                    onParticipantClick={onParticipantClick}
                    setLiveChatUserId={setLiveChatUserId}
                    setAgentCheckpointsUserId={setAgentCheckpointsUserId}
                    pinnedParticipantId={pinnedParticipantId}
                    pinnedTrackSid={pinnedTrackSid}
                    liveSessionType={liveSession?.live_room?.type}
                    tracksMap={tracksMap}
                />
            ) : (
                <TutorGridLayoutM
                    setPinnedMode={setPinnedMode}
                    onTrackClick={onTrackClick}
                    onParticipantClick={onParticipantClick}
                    setLiveChatUserId={setLiveChatUserId}
                    setAgentCheckpointsUserId={setAgentCheckpointsUserId}
                    availableWindowHeight={availableWindowHeight}
                    participants={otherParticipants}
                    tracksMap={tracksMap}
                />
            )}
            {hasFeature(liveSession?.live_room, "checkpoint-rh") && (
                <ModalM
                    visible={agentCheckpointsUserId !== null}
                    onDismiss={() => setAgentCheckpointsUserId(null)}
                    style={{
                        width: dimensions?.width,
                        height: "70%",
                        overflow: "hidden",
                    }}
                >
                    <AgentSummaryModal
                        student={participants?.find(
                            (x) => x?.id === agentCheckpointsUserId
                        )}
                        navigation={navigation}
                        onExit={() => {
                            setAgentCheckpointsUserId(null)
                        }}
                    />
                </ModalM>
            )}

            <ModalM
                visible={liveChatUserId !== null}
                onDismiss={() => setLiveChatUserId(null)}
                style={{
                    width: dimensions?.width,
                    height: "90%",
                    overflow: "hidden",
                }}
            >
                <LiveChat
                    userId={liveChatUserId}
                    onExit={() => {
                        setLiveChatUserId(null)
                    }}
                    nav={navigation}
                    route={route}
                    style={{}}
                />
            </ModalM>
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        liveSessions: state.data.currentUser?.object?.live_sessions,
        liveSessionId: state.liveMode?.liveSessionId,
    }
}

export default connect(mapStateToProps)(TutorRoomView)
